import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ABLE_COLORS } from '../constants';

/**
 * Tooltip - Custom styled tooltip that renders outside parent containers
 * Uses portal to avoid being clipped by overflow:hidden parents
 */
export default function Tooltip({ children, content, position = 'top' }) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipEl = tooltipRef.current;

      // Calculate position based on trigger element
      let top, left;

      if (position === 'top') {
        top = rect.top - 8; // 8px gap
        left = rect.left + rect.width / 2;
      } else if (position === 'bottom') {
        top = rect.bottom + 8;
        left = rect.left + rect.width / 2;
      }

      setCoords({ top, left });

      // Adjust if tooltip goes off screen (after render)
      if (tooltipEl) {
        const tooltipRect = tooltipEl.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        // Prevent going off left edge
        if (tooltipRect.left < 8) {
          setCoords(prev => ({ ...prev, left: tooltipRect.width / 2 + 8 }));
        }
        // Prevent going off right edge
        if (tooltipRect.right > viewportWidth - 8) {
          setCoords(prev => ({ ...prev, left: viewportWidth - tooltipRect.width / 2 - 8 }));
        }
      }
    }
  }, [isVisible, position]);

  const tooltipElement = isVisible && (
    <div
      ref={tooltipRef}
      style={{
        position: 'fixed',
        top: position === 'top' ? coords.top : coords.top,
        left: coords.left,
        transform: position === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
        background: ABLE_COLORS.textPrimary,
        color: ABLE_COLORS.neutral,
        padding: '8px 12px',
        borderRadius: ABLE_COLORS.radiusSmall,
        fontSize: '12px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 10000,
        maxWidth: '280px',
        textAlign: 'center',
        lineHeight: 1.4,
        pointerEvents: 'none',
        whiteSpace: 'normal',
        wordBreak: 'break-word'
      }}
    >
      {content}
      {/* Arrow */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          ...(position === 'top' ? {
            bottom: '-6px',
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: `6px solid ${ABLE_COLORS.textPrimary}`
          } : {
            top: '-6px',
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderBottom: `6px solid ${ABLE_COLORS.textPrimary}`
          })
        }}
      />
    </div>
  );

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        style={{ display: 'inline-flex', alignItems: 'center' }}
      >
        {children}
      </span>
      {createPortal(tooltipElement, document.body)}
    </>
  );
}
